import {
  Modal,
  message,
  Row,
  Col,
  Input,
  Spin,
  Button,
  Card,
  Space,
  Table,
  notification,
  Typography,
  Tag,
} from "antd";
import { useEffect, useState } from "react";
import axios from "axios";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import dayjs from "dayjs";
import {
  BASE_URL_BEN,
  GET_GEO_ALL,
  HEADERS_EXPORT_BEN,
  POST_SEND_RESPONE_INVESTIGATE,
} from "../../../API/apiUrls";
import {
  SearchOutlined,
  RestOutlined,
  FileExcelOutlined,
} from "@ant-design/icons";
import AddRelationAssets from "./AddRelationAssets";

const { Text } = Typography;

const convertToTargetFormat = (data, dataDefault) => {
  if (!Array.isArray(dataDefault)) {
    return { validData: [], invalidData: [] };
  }

  const validData = [];
  const invalidData = [];
  const allData = [];
  const errors = [];

  const isGenericName = (name) => {
    const badPatterns = [
      /^ต(\.|ำบล)?$/i, // ต. หรือ ตำบล
      /^อ(\.|ำเภอ)?$/i, // อ. หรือ อำเภอ
      /^จ(\.|ังหวัด)?$/i, // จ. หรือ จังหวัด
    ];
    return badPatterns.some((regex) => regex.test(name.trim()));
  };
  // ฟังก์ชัน normalize text (ลบเครื่องหมายและคำนำหน้า)
  const normalizeText = (text) => {
    if (!text) return "";
    return (
      text
        // ตัดคำนำหน้า: จ., จังหวัด, อ., อำเภอ, เขต, ต., ตำบล, แขวง, ห้องชุดเลขที่
        .replace(
          /^(จ\.|จังหวัด|อ\.|อำเภอ|เขต|ต\.|ตำบล|แขวง|ห้องชุดเลขที่|ห้องชุด)/g,
          "",
        )
        .trim()
        .replace(/์/g, "")
        .replace(/\s+/g, "")
        .toLowerCase()
    );
  };

  // ฟังก์ชันคำนวณความคล้าย (Levenshtein Distance)
  const similarity = (s1, s2) => {
    const longer = s1.length > s2.length ? s1 : s2;
    const shorter = s1.length > s2.length ? s2 : s1;

    if (longer.length === 0) return 1.0;

    const editDistance = levenshteinDistance(longer, shorter);
    return (longer.length - editDistance) / longer.length;
  };

  const levenshteinDistance = (str1, str2) => {
    const matrix = [];

    for (let i = 0; i <= str2.length; i++) {
      matrix[i] = [i];
    }

    for (let j = 0; j <= str1.length; j++) {
      matrix[0][j] = j;
    }

    for (let i = 1; i <= str2.length; i++) {
      for (let j = 1; j <= str1.length; j++) {
        if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1,
          );
        }
      }
    }

    return matrix[str2.length][str1.length];
  };

  const getProvinceCode = (provinceName, dataItem, itemErrors) => {
    if (!provinceName || isGenericName(provinceName)) {
      const error = {
        type: "province",
        message: "ไม่พบข้อมูลจังหวัด",
        data: { provinceName: provinceName || "ไม่ระบุ" },
        rawData: dataItem,
      };
      errors.push(error);
      itemErrors.push(error);
      return null;
    }

    const normalizedInput = normalizeText(provinceName);
    let provinceFilter = data.province.find((item) =>
      normalizeText(item.provDesc).includes(normalizedInput),
    );

    if (!provinceFilter) {
      let bestMatch = null;
      let bestScore = 0;
      data.province.forEach((item) => {
        const score = similarity(normalizedInput, normalizeText(item.provDesc));
        if (score > bestScore && score >= 0.75) {
          bestScore = score;
          bestMatch = item;
        }
      });
      if (bestMatch) {
        console.warn(
          `✅ จังหวัด "${provinceName}" → "${bestMatch.provDesc}" (${Math.round(
            bestScore * 100,
          )}%)`,
        );
        provinceFilter = bestMatch;
      }
    }

    if (!provinceFilter) {
      const error = {
        type: "province",
        message: "ไม่พบข้อมูลจังหวัด",
        data: { provinceName },
        rawData: dataItem,
      };
      errors.push(error);
      itemErrors.push(error);
      return null;
    }

    return provinceFilter;
  };

  const getDistrictCode = (
    districtName,
    provinceCode,
    dataItem,
    itemErrors,
  ) => {
    if (!districtName || isGenericName(districtName)) {
      const error = {
        type: "district",
        message: "ไม่พบข้อมูลอำเภอ",
        data: { districtName: districtName || "ไม่ระบุ" },
        rawData: dataItem,
      };
      errors.push(error);
      itemErrors.push(error);
      return null;
    }

    if (!provinceCode) return null;

    const normalizedInput = normalizeText(districtName);
    const districtsInProvince = data.district.filter(
      (item) => item.provCode === provinceCode.provCode.toString(),
    );

    let districtFilter = districtsInProvince.find((item) =>
      normalizeText(item.distDesc).includes(normalizedInput),
    );

    if (!districtFilter) {
      let bestMatch = null;
      let bestScore = 0;
      districtsInProvince.forEach((item) => {
        const score = similarity(normalizedInput, normalizeText(item.distDesc));
        if (score > bestScore && score >= 0.75) {
          bestScore = score;
          bestMatch = item;
        }
      });
      if (bestMatch) {
        console.warn(
          `✅ อำเภอ "${districtName}" → "${bestMatch.distDesc}" (${Math.round(
            bestScore * 100,
          )}%)`,
        );
        districtFilter = bestMatch;
      }
    }

    if (!districtFilter) {
      const error = {
        type: "district",
        message: "ไม่พบข้อมูลอำเภอ",
        data: { districtName },
        rawData: dataItem,
      };
      errors.push(error);
      itemErrors.push(error);
      return null;
    }

    return districtFilter;
  };

  const getSubdistrictCode = (
    subdistrictName,
    provinceCode,
    districtCode,
    dataItem,
    itemErrors,
  ) => {
    if (!subdistrictName || isGenericName(subdistrictName)) {
      return null;
    }

    if (!provinceCode || !districtCode) return null;

    const normalizedInput = normalizeText(subdistrictName);
    const subDistrictsFiltered = data.subDistrict.filter(
      (item) =>
        item.distCode === districtCode.distCode &&
        item.provCode === provinceCode.provCode.toString(),
    );

    let subdistrictFilter = subDistrictsFiltered.find((item) =>
      normalizeText(item.subDistDesc).includes(normalizedInput),
    );

    if (!subdistrictFilter) {
      let bestMatch = null;
      let bestScore = 0;
      subDistrictsFiltered.forEach((item) => {
        const score = similarity(
          normalizedInput,
          normalizeText(item.subDistDesc),
        );
        if (score > bestScore && score >= 0.8) {
          bestScore = score;
          bestMatch = item;
        }
      });
      if (bestMatch) {
        console.warn(
          `✅ ตำบล "${subdistrictName}" → "${
            bestMatch.subDistDesc
          }" (${Math.round(bestScore * 100)}%)`,
        );
        subdistrictFilter = bestMatch;
      }
    }

    if (!subdistrictFilter) {
      const error = {
        type: "subdistrict",
        message: "ไม่พบข้อมูลตำบล",
        data: {
          subdistrictName,
          districtName: districtCode.distDesc,
          provinceName: provinceCode.provDesc,
        },
        rawData: dataItem,
      };
      errors.push(error);
      itemErrors.push(error);
      return null;
    }

    return subdistrictFilter;
  };

  // ฟังก์ชันแยก assetLand จากข้อความ mark
  const parseasset = (markText, rawItem, itemErrors) => {
    if (!markText || markText.trim() === "") return [];
    const assets = [];

    // ---------- utils ----------
    const toArabic = (s = "") =>
      s.replace(/[๐-๙]/g, (d) => "๐๑๒๓๔๕๖๗๘๙".indexOf(d));

    const fixCommonTypos = (s = "") =>
      s
        .replace(/อ\.?มือง/g, "อ.เมือง")
        .replace(/อําเภอ/g, "อำเภอ")
        .replace(/ตําบล/g, "ตำบล");

    // ตัดคำ marker ออก (กัน regex location ไปติด marker)
    const stripMarkers = (s = "") =>
      s
        .replace(/ห้องชุด(?:\s*เลขที่)?/gi, "")
        .replace(/อ\.?\s*ช\.?\s*(?:2\.?\s*ป?|ป)/gi, "")
        .replace(/อาคารชุด/gi, "")
        .replace(/น\.?\s*ส\.?\s*\d*\s*ก?\.?/gi, "")
        .replace(/ฉ\.?/gi, "")
        .trim();

    // ดึงเลข: รองรับ 12, 12/3, คั่นด้วย comma/space และเลขไทย
    const pickNumbers = (s = "") => {
      const nums = toArabic(s).match(/[0-9]+(?:\/[0-9]+)?/g) || [];
      return nums.map((x) => x.trim()).filter(Boolean);
    };

    // ดึงที่ตั้งแบบ “ระวัง boundary” กันติดคำอื่น เช่น "ห้องชุด"
    const extractLocation = (input = "") => {
      const txt = fixCommonTypos(input);
      const pv = txt.match(/(?:^|[\s,])(?:จังหวัด|จ\.)\s*([^\s,]+)/i);
      const dt = txt.match(
        /(?:^|[\s,])(?:อ\.?|อำเภอ|เขต)(?:\s+|\.)?([^\s,]+)/i,
      );
      const sd = txt.match(
        /(?:^|[\s,])(?:ต\.?|ตำบล|แขวง)(?:\s+|\.)?([^\s,]+)/i,
      );
      return {
        provinceName: pv ? pv[1].trim() : "",
        districtName: dt ? dt[1].trim() : "",
        subdistrictName: sd ? sd[1].trim() : "",
      };
    };

    // ---------- แยกเซกเมนต์ ----------
    const text = markText;

    // marker ของชนิดเอกสาร
    const MARKER = String.raw`(?:ห้องชุด(?:\s*เลขที่)?|อ\.?\s*ช\.?\s*(?:2\.?\s*ป?|ป)|อาคารชุด|น\.?\s*ส\.?\s*\d*\s*ก?\.?|ฉ\.?)`;

    // แบ่งเมื่อเจอ marker ใหม่ หรือ comma+digit ที่เริ่มกลุ่มเลขชุดใหม่
    // หมายเหตุ: เรา “คง marker ล่าสุด” ให้เลขชุดต่อ ๆ ไปจนกว่าจะเจอ marker ใหม่
    const BOUNDARY_RE = new RegExp(`(${MARKER})|,\\s*(?=[0-9๐-๙])`, "gi");

    const boundaries = [];
    let m;
    while ((m = BOUNDARY_RE.exec(text)) !== null) {
      boundaries.push({ index: m.index, marker: m[1] || null, match: m[0] });
    }

    const segments = [];
    let lastIdx = 0;
    let lastMarker = null;

    for (let i = 0; i < boundaries.length; i++) {
      const b = boundaries[i];
      if (b.index > lastIdx) {
        segments.push({
          marker: lastMarker, // ใช้ marker ล่าสุดให้ payload นี้
          payload: text.slice(lastIdx, b.index).trim(),
        });
      }
      if (b.marker) {
        // เจอ marker ใหม่ → อัปเดต marker ปัจจุบัน
        lastMarker = b.marker.trim();
        lastIdx = b.index + b.match.length;
      } else {
        // เป็น comma+digit → ข้าม comma เริ่มกลุ่มตัวเลขถัดไป (marker เดิม)
        lastIdx = b.index + b.match.length;
      }
    }
    if (lastIdx < text.length) {
      segments.push({
        marker: lastMarker,
        payload: text.slice(lastIdx).trim(),
      });
    }
    if (segments.length === 0) {
      segments.push({ marker: null, payload: text.trim() });
    }

    // ---------- tail location (fallback ท้ายบรรทัด) ----------
    const tailLoc = (() => {
      const loc = extractLocation(stripMarkers(text));
      const hasAny =
        loc.provinceName || loc.districtName || loc.subdistrictName;
      return hasAny
        ? loc
        : { provinceName: "", districtName: "", subdistrictName: "" };
    })();

    // ---------- เตรียม chunks: แยกชนิดเอกสาร / เลข / ที่ตั้งใน segment ----------
    const chunks = [];
    let prevType = 1; // ดีฟอลต์ "ฉ."
    segments.forEach(({ marker, payload }) => {
      if (!payload) return;

      // 1) ชนิดเอกสาร
      let landType = prevType;
      if (marker) {
        if (
          /ห้องชุด/i.test(marker) ||
          /อ\.?\s*ช\.?\s*(?:2\.?\s*ป?|ป)/i.test(marker) // อ.ช.2, อ.ช.2.ป, อช2ป, อชป
        )
          landType = 3;
        else if (/อาคารชุด/i.test(marker)) landType = 4;
        else if (/น\.?\s*ส\.?\s*4/i.test(marker))
          landType = 1; // นส.4 = โฉนด
        else if (/น\.?\s*ส/i.test(marker))
          landType = 2; // นส.3 ต่างๆ (3, 3ก, 3ข ฯลฯ)
        else if (/ฉ/i.test(marker)) landType = 1;
        prevType = landType;
      }

      // 2) เลขใน payload
      const numbers = pickNumbers(payload);
      if (numbers.length === 0) return;

      // 3) ที่ตั้งเฉพาะใน payload (strip marker ออกก่อนกันชน)
      const segLoc = extractLocation(stripMarkers(payload));

      chunks.push({ landType, numbers, segLoc });
    });

    // ---------- Forward field-wise inheritance ----------
    // ถ้า chunk หนึ่งไม่มี province/district/subdistrict → ยืม “เฉพาะฟิลด์นั้น” จาก chunk ทางขวา (ถัดไป)
    const forwardLoc = new Array(chunks.length).fill(null);
    let carry = { provinceName: "", districtName: "", subdistrictName: "" };

    for (let i = chunks.length - 1; i >= 0; i--) {
      const loc = chunks[i].segLoc || {
        provinceName: "",
        districtName: "",
        subdistrictName: "",
      };
      carry = {
        provinceName: loc.provinceName || carry.provinceName || "",
        districtName: loc.districtName || carry.districtName || "",
        subdistrictName: loc.subdistrictName || carry.subdistrictName || "",
      };
      forwardLoc[i] = carry;
    }

    // ---------- สร้าง assets ----------
    chunks.forEach((ch, idx) => {
      const useLoc = {
        // ใช้ลำดับ: segLoc > forwardLoc (ขวา) > tailLoc
        provinceName:
          ch.segLoc?.provinceName ||
          forwardLoc[idx]?.provinceName ||
          tailLoc.provinceName ||
          "",
        districtName:
          ch.segLoc?.districtName ||
          forwardLoc[idx]?.districtName ||
          tailLoc.districtName ||
          "",
        subdistrictName:
          ch.segLoc?.subdistrictName ||
          forwardLoc[idx]?.subdistrictName ||
          tailLoc.subdistrictName ||
          "",
      };

      // map codes
      const provinceCode = getProvinceCode(
        useLoc.provinceName,
        rawItem,
        itemErrors,
      );
      const districtCode = getDistrictCode(
        useLoc.districtName,
        provinceCode,
        rawItem,
        itemErrors,
      );
      const subdistrictCode = getSubdistrictCode(
        useLoc.subdistrictName,
        provinceCode,
        districtCode,
        rawItem,
        itemErrors,
      );

      ch.numbers.forEach((num) => {
        assets.push({
          typeLand: ch.landType,
          numberLand: num,
          province: provinceCode?.provCode?.toString() || null,
          provinceName: provinceCode?.provDesc || useLoc.provinceName || null,
          district: districtCode?.distCode || null,
          districtName: districtCode?.distDesc || useLoc.districtName || null,
          subdistrict: subdistrictCode?.subDistCode || null,
          subdistrictName:
            subdistrictCode?.subDistDesc || useLoc.subdistrictName || null,
        });
      });
    });

    return assets;
  };

  // ฟังก์ชันแยกชื่อและคำนำหน้า
  const parseFullname = (fullname) => {
    if (!fullname) return { snam: "", name1: "", name2: "", relation: null };

    const prefixes = ["นางสาว", "นาย", "นาง"];

    let relation = null;
    // Pattern 1: ดึงจาก (บุตร) หรือ (คู่สมรส)
    const relationMatch1 = fullname.match(/\(([^)]+)\)/);

    if (relationMatch1) {
      relation =
        relationMatch1[1].trim() === "สามี" ||
        relationMatch1[1].trim() === "ภรรยา" ||
        relationMatch1[1].trim() === "คู่สมรส"
          ? "คู่สมรส"
          : "บุตรไม่บรรลุนิติภาวะ";
    }

    // Pattern 2: ดึงจากหลัง , เช่น "นาย สมชาย รักดี, บุตร"
    if (!relation) {
      const relationMatch2 = fullname.match(/,\s*([^,()]+)$/);
      if (relationMatch2) {
        const possibleRelation = relationMatch2[1].trim();
        // เช็คว่าไม่ใช่ชื่อคน (ไม่มีคำนำหน้าชื่อ)
        const hasPrefix = prefixes.some((p) => possibleRelation.startsWith(p));
        if (!hasPrefix) {
          relation = possibleRelation;
        }
      }
    }

    // ตัดส่วนใน () หรือหลัง , ออก
    let cleanName = fullname.split(/[,()]/)[0].trim();

    let snam = "";
    let remainingName = cleanName;

    // หาคำนำหน้าชื่อ
    for (const prefix of prefixes) {
      if (cleanName.startsWith(prefix)) {
        snam = prefix;
        remainingName = cleanName.substring(prefix.length).trim();
        break;
      }
    }

    // แยกชื่อและนามสกุล
    const nameParts = remainingName.split(/\s+/).filter((p) => p);
    const name1 = nameParts[0] || "";
    const name2 = nameParts.slice(1).join(" ") || "";

    return { snam, name1, name2, relation };
  };

  // ตัวแปรเก็บค่า loanId, invId และ customerId จากตัวก่อนหน้า
  let lastLoanId = null;
  let lastInvId = null;
  let lastCustomerId = null;
  let lastContnoGroup = null;
  let lastroundId = null;
  let lastSchema = null;
  let cusCodeNew = null;
  let lastGarno = null;
  // ประมวลผลข้อมูล
  dataDefault.forEach((item, itemIndex) => {
    // สร้าง array สำหรับเก็บ errors ของ item นี้
    const itemErrors = [];

    const { snam, name1, name2, relation } = parseFullname(
      item.customerFullname,
    );

    let assetLand = [];

    assetLand = parseasset(item.mark, item, itemErrors);

    // ถ้า contnoGroup เปลี่ยน ให้รีเซ็ตค่าทั้งหมด
    if (item.contnoGroup !== lastContnoGroup) {
      lastLoanId = null;
      lastInvId = null;
      lastCustomerId = null;
      lastroundId = null;
      lastContnoGroup = item.contnoGroup;
      lastSchema = item.SCHEMA;
    }

    // ถ้าตัวปัจจุบันมี loanId/invId ให้ใช้ค่านั้น, ถ้าไม่มีให้ใช้ค่าจากตัวก่อนหน้า
    let currentLoanId = item.loanId || lastLoanId || 0;
    let currentInvId = item.inv_id || lastInvId || 0;
    let currentRoundId = item.roundId || lastroundId || 0;
    let curentSchema = item.SCHEMA || lastSchema;
    let curentGarno = item.garno || lastGarno;
    // กำหนด relationCustomerId
    let relationCustomerId = 0;

    if (!item.cusId || item.cusId === "" || item.cusId === 0) {
      // ถ้าไม่มี customerId ให้ใช้ค่าจากตัวก่อนหน้า
      relationCustomerId = lastCustomerId || 0;
      curentSchema = lastSchema;
    } else {
      // ถ้ามี customerId ให้ relationCustomerId = 0
      relationCustomerId = 0;
    }

    // อัปเดตค่าให้เตรียมสำหรับ item ถัดไป
    if (item.loanId) {
      lastLoanId = item.loanId;
    }
    if (item.inv_id) {
      lastInvId = item.inv_id;
    }
    if (item.cusId && item.cusId !== "" && item.cusId !== 0) {
      lastCustomerId = item.cusId;
    }
    if (item.roundId) {
      lastroundId = item.roundId;
    }
    const genUID13 = () => {
      const array = new Uint8Array(5);
      crypto.getRandomValues(array);
      const digits = Array.from(array)
        .map((b) => b.toString(10).padStart(2, "0"))
        .join("")
        .slice(0, 10);

      return "gen" + digits;
    };

    if (item.cusCode && item.cusCode !== "" && item.cusCode !== 0) {
      cusCodeNew = item.cusCode;
    } else {
      cusCodeNew = genUID13();
    }

    if (item.garno) {
      lastGarno = item.garno;
    }

    const convertedItem = {
      key: itemIndex++,
      noExcel: item.noExcel,
      garno: curentGarno,
      SCHEMA: curentSchema,
      contnoGroup: item.contnoGroup,
      loanId: currentLoanId,
      assetOnlineId: item.asset_online_id || 0,
      invId: currentInvId,
      roundId: currentRoundId,
      customerId: item.cusId || 0,
      investigation: assetLand.length > 0 ? true : false,
      SNAM: snam,
      NAME1: name1,
      NAME2: name2,
      CUSCOD: cusCodeNew,
      relation: relation ? relation : "-",
      relationCustomerId: relationCustomerId,
      assetLand: assetLand,
      errors: itemErrors, // ✅ เพิ่ม errors ลงไป
      moneyOnline: {
        tr14: item.tr14 || 0,
        familyRegistration: item.family_registration || 0,
        operationFee: item.operation_fee || 0,
        spouseFee: 0,
        additionalFee: item.additional_fee || 0,
        total: item.total || 0,
      },
    };

    if (item.cusId === "" || !item.cusId) {
      invalidData.push(convertedItem);
    } else {
      validData.push(convertedItem);
    }
    allData.push(convertedItem);
  });

  return { validData, invalidData, allData, errors };
};

const ImportInvestigate = ({ open, close, dataDefault, func }) => {
  const [loading, setLoading] = useState(false);
  const [arrayTable, setArrayTable] = useState(dataDefault);
  const [searchInput, setSearchInput] = useState(null);
  const [preData, setPreData] = useState();
  const [isModalInvalid, setIsModalInvalid] = useState(false);
  const [geoRawData, setGeoRawData] = useState();
  const [sumMoney, setSumMoney] = useState({});
  const [sumMoneyEstimate, setSumMoneyEstimate] = useState({});
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);

  useEffect(() => {
    const loadGeoData = async () => {
      setLoading(true);
      try {
        const res = await axios.get(GET_GEO_ALL, {
          headers: HEADERS_EXPORT_BEN,
        });
        setGeoRawData(res.data);
        const dataSend = convertToTargetFormat(res.data, dataDefault);
        console.log("dataSend", dataSend);
        setPreData(dataSend);
      } catch (error) {
        console.error("Error loading data:", error);
        message.error(`ไม่พบข้อมูล: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };

    loadGeoData();

    checkError();
    calculateMoney();
  }, [dataDefault]);

  const checkError = () => {
    const missingFoundData = dataDefault.filter(
      (item) => item.cusCode === undefined || item.cusCode === "",
    );
    console.log("missingFoundData", missingFoundData);

    if (missingFoundData.length > 0) {
      const missingList = (
        <ul style={{ marginLeft: 20 }}>
          {missingFoundData.map((d, index) => (
            <li key={index}>
              <strong>{d.contno || "-"}</strong>
              <Text copyable> {d.customerFullname || "ไม่ระบุชื่อ"}</Text>
            </li>
          ))}
        </ul>
      );
      notification.error({
        message: "พบข้อมูลไม่ครบถ้วน",
        description: (
          <>
            <div style={{ marginBottom: 8 }}>
              แถวต่อไปนี้ไม่มีข้อมูล <b>รหัสบัตร</b>:
            </div>
            <div
              style={{
                maxHeight: "100px",
                overflowY: "auto",
                marginBottom: 12,
              }}
            >
              {missingList}
            </div>
            {/* ส่วนที่ทำหน้าที่เป็น Footer */}
            <div
              style={{
                borderTop: "1px solid #f0f0f0",
                paddingTop: 8,
                marginTop: 8,
                fontSize: "12px",
                color: "rgba(0, 0, 0, 0.45)",
              }}
            >
              โปรดแก้ไขใน excel ใหม่หรือให้ระบบสร้างขึ้นให้อัตโนมัติ
            </div>
          </>
        ),
        duration: 20,
      });
    }
  };

  const calculateMoney = () => {
    const totals = dataDefault.reduce(
      (acc, item) => {
        return {
          tr14Sum: acc.tr14Sum + (Number(item.tr14Excel) || 0),
          familyRegistrationSum:
            acc.familyRegistrationSum +
            (Number(item.family_registrationExcel) || 0),
          operationFeeSum:
            acc.operationFeeSum + (Number(item.operation_feeExcel) || 0),
          additionalFeeSum:
            acc.additionalFeeSum + (Number(item.additional_feeExcel) || 0),
          totalSum: acc.totalSum + (Number(item.totalExcel) || 0),
        };
      },
      {
        tr14Sum: 0,
        familyRegistrationSum: 0,
        operationFeeSum: 0,
        additionalFeeSum: 0,
        totalSum: 0,
      },
    );

    const totalsEstimate = dataDefault.reduce(
      (acc, item) => {
        return {
          tr14Sum: acc.tr14Sum + (Number(item.tr14) || 0),
          familyRegistrationSum:
            acc.familyRegistrationSum + (Number(item.family_registration) || 0),
          operationFeeSum:
            acc.operationFeeSum + (Number(item.operation_fee) || 0),
          additionalFeeSum:
            acc.additionalFeeSum + (Number(item.additional_fee) || 0),
          totalSum: acc.totalSum + (Number(item.total) || 0),
        };
      },
      {
        tr14Sum: 0,
        familyRegistrationSum: 0,
        operationFeeSum: 0,
        additionalFeeSum: 0,
        totalSum: 0,
      },
    );

    setSumMoney(totals);
    setSumMoneyEstimate(totalsEstimate);

    console.log("รวมเงินทั้งหมด:", totals);
    console.log("รวมเงินทั้งหมด totalsEstimate:", totalsEstimate);
    console.log("dataDefault", dataDefault);
    console.log("preData", preData);
  };
  const handleCancel = (status, value) => {
    func(status, value);
    if (status) {
      close();
    }
  };

  const onChangeInputSearch = (data) => {
    setSearchInput(data);
  };

  const handleOk = () => {
    setLoading(true);
    const payload = preData.validData;
    console.log("preData=====>", preData);
    setIsModalInvalid(true);
    setLoading(false);
    // if (preData.invalidData.length > 0) {
    //   setIsModalInvalid(true);
    // } else {
    //   postData(payload);
    //   close();
    // }
  };

  //ไม่เปิดใช้งาน
  // const postData = async (rawData) => {
  //   setLoading(true);
  //   try {
  //     const res = await axios.post(
  //       BASE_URL_BEN + POST_SEND_RESPONE_INVESTIGATE,
  //       rawData,
  //       {
  //         headers: HEADERS_EXPORT_BEN,
  //       },
  //     );

  //     const { status, data } = res;
  //     console.log("postData response:", data);

  //     if (status === 200) {
  //       message.success(`บันทึกสำเร็จ`);
  //       return data; // return ข้อมูลกลับไป
  //     } else if (status === 201) {
  //       message.warning(String(data?.OK));
  //       message.success(`บันทึกสำเร็จ`);
  //       return data;
  //     } else {
  //       message.success(`บันทึกสำเร็จ`);
  //       return data;
  //     }
  //   } catch (error) {
  //     const status = error?.response?.status;
  //     const serverMsg =
  //       error?.response?.data?.message ||
  //       error?.response?.data?.error ||
  //       error.message;

  //     if (status === 404) {
  //       message.error("ไม่พบปลายทาง (404)");
  //     } else if (status === 400) {
  //       message.error(`ข้อมูลไม่ถูกต้อง: ${serverMsg}`);
  //     } else if (status === 500) {
  //       message.error("เกิดข้อผิดพลาดที่เซิร์ฟเวอร์");
  //     } else {
  //       message.error(
  //         `ไม่สามารถส่งข้อมูลได้${status ? ` (${status})` : ""}: ${serverMsg}`,
  //       );
  //     }

  //     console.error("postData error:", error);
  //     throw error; // โยน error ไปให้ handleSave จัดการ
  //   }
  // };

  const handleExportExcel = async () => {
    if (!arrayTable || arrayTable.length === 0) {
      message.warning("ไม่มีข้อมูลสำหรับ Export");
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const ws = workbook.addWorksheet("นำเข้าข้อมูลสืบทรัพย์ออนไลน์");

    ws.columns = [
      { header: "ลำดับ", key: "no", width: 8 },
      { header: "ลำดับเอกสาร", key: "noExcel", width: 14 },
      { header: "เลขที่สัญญา", key: "contno", width: 18 },
      { header: "รายชื่อ", key: "customerFullname", width: 28 },
      { header: "เลขบัตร", key: "cusCode", width: 20 },
      { header: "สถานะ", key: "status", width: 10 },
      { header: "ค่า ทร.14", key: "tr14Excel", width: 14 },
      { header: "ค่า ทร.14(ระบบ)", key: "tr14", width: 14 },
      { header: "ทะเบียนครอบครัว", key: "familyRegistrationExcel", width: 16 },
      {
        header: "ทะเบียนครอบครัว(ระบบ)",
        key: "familyRegistration",
        width: 18,
      },
      { header: "ดำเนินงาน", key: "operationFeeExcel", width: 14 },
      { header: "ดำเนินงาน(ระบบ)", key: "operationFee", width: 16 },
      { header: "ตรวจเพิ่ม", key: "additionalFeeExcel", width: 14 },
      { header: "ตรวจเพิ่ม(ระบบ)", key: "additionalFee", width: 16 },
      { header: "รวม", key: "totalExcel", width: 14 },
      { header: "หมายเหตุ", key: "mark", width: 45 },
    ];

    arrayTable.forEach((item, index) => {
      const row = ws.addRow({
        no: index + 1,
        noExcel: item.noExcel,
        contno: item.contno,
        customerFullname: item.customerFullname,
        cusCode: item.cusCode,
        status: item.unFound === "√" ? "x" : item.found === "√" ? "√" : "",
        tr14Excel: item.tr14Excel,
        tr14: item.tr14,
        familyRegistrationExcel: item.family_registrationExcel,
        familyRegistration: item.family_registration,
        operationFeeExcel: item.operation_feeExcel,
        operationFee: item.operation_fee,
        additionalFeeExcel: item.additional_feeExcel,
        additionalFee: item.additional_fee,
        totalExcel: item.totalExcel,
        mark: item.mark,
      });

      row.eachCell({ includeEmpty: true }, (cell) => {
        cell.alignment = {
          vertical: "middle",
          horizontal: "center",
          wrapText: true,
        };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });
    });

    const header = ws.getRow(1);
    header.height = 20;
    header.eachCell({ includeEmpty: true }, (cell) => {
      cell.alignment = { vertical: "middle", horizontal: "center" };
      cell.font = { bold: true };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFEFEFEF" },
      };
      cell.border = {
        top: { style: "medium" },
        left: { style: "thin" },
        bottom: { style: "medium" },
        right: { style: "thin" },
      };
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(
      blob,
      `นำเข้าข้อมูลสืบทรัพย์ออนไลน์_${dayjs().format("DD_MM_YY_HHmmss")}.xlsx`,
    );
  };

  const onQueryData = () => {
    // 1) ตั้งต้นจาก data เดิม
    let result = Array.isArray(dataDefault) ? [...dataDefault] : [];

    // 2) ค้นหาด้วยข้อความ (เลขสัญญา)
    const q = (searchInput ?? "").trim().toLowerCase();
    if (q) {
      result = result.filter(
        (item) =>
          String(item.contno ?? "")
            .toLowerCase()
            .includes(q) ||
          String(item.customerFullname ?? "").includes(q) ||
          String(item.cusCode ?? "").includes(q) ||
          String(item.mark ?? "").includes(q),
      );
    }
    setArrayTable(result);
    console.log(result);
  };

  const columns = [
    {
      title: "ลำดับ",
      key: "index",
      align: "center",
      render: (text, record, index) => <>{index + 1}</>,
    },
    {
      title: "ลำดับเอกสาร",
      key: "index",
      align: "center",
      render: (text, record) => <Tag color="geekblue">{record.noExcel}</Tag>,
    },
    {
      title: "เลขที่สัญญา",
      dataIndex: "contno",
      key: "contno",
      align: "center",
      render: (text, record) => <>{record.contno ? record.contno : null}</>,
    },
    {
      title: "รายชื่อ",
      align: "center",
      render: (text, record) => <>{record.customerFullname}</>,
    },
    {
      title: "เลขบัตร",
      align: "center",
      render: (text, record) => <>{record.cusCode}</>,
    },

    {
      title: "สถานะ",
      align: "center",
      render: (text, record) => (
        <>{record.unFound === "√" ? "x" : record.found === "√" ? "√" : null}</>
      ),
    },
    // {
    //   title: "หมายเหตุ",
    //   align: "center",
    //   render: (text, record) => (
    //     <p
    //       style={{
    //         wordBreak: "break-word",
    //       }}
    //     >
    //       {record.mark}
    //     </p>
    //   ),
    // },
    {
      title: "ค่า ทร.14",
      align: "center",
      render: (_, record) => (
        <span
          style={
            record.tr14Excel !== record.tr14
              ? { color: "red", fontWeight: "bold" }
              : {}
          }
        >
          {record.tr14Excel}
        </span>
      ),
    },
    {
      title: "ค่า ทร.14(ระบบ)",
      align: "center",
      render: (_, record) => (
        <span
          style={
            record.tr14Excel !== record.tr14
              ? { color: "red", fontWeight: "bold" }
              : {}
          }
        >
          {record.tr14}
        </span>
      ),
    },

    // ทะเบียนครอบครัว
    {
      title: "ทะเบียนครอบครัว",
      align: "center",
      render: (_, record) => (
        <span
          style={
            record.family_registrationExcel !== record.family_registration
              ? { color: "red", fontWeight: "bold" }
              : {}
          }
        >
          {record.family_registrationExcel}
        </span>
      ),
    },
    {
      title: "ทะเบียนครอบครัว(ระบบ)",
      align: "center",
      render: (_, record) => (
        <span
          style={
            record.family_registrationExcel !== record.family_registration
              ? { color: "red", fontWeight: "bold" }
              : {}
          }
        >
          {record.family_registration}
        </span>
      ),
    },

    // ดำเนินงาน
    {
      title: "ดำเนินงาน",
      align: "center",
      render: (_, record) => (
        <span
          style={
            record.operation_feeExcel !== record.operation_fee
              ? { color: "red", fontWeight: "bold" }
              : {}
          }
        >
          {record.operation_feeExcel}
        </span>
      ),
    },
    {
      title: "ดำเนินงาน(ระบบ)",
      align: "center",
      render: (_, record) => (
        <span
          style={
            record.operation_feeExcel !== record.operation_fee
              ? { color: "red", fontWeight: "bold" }
              : {}
          }
        >
          {record.operation_fee}
        </span>
      ),
    },
    {
      title: "ตรวจเพิ่ม",
      align: "center",
      render: (_, record) => (
        <span
          style={
            record.additional_feeExcel !== record.additional_fee
              ? { color: "red", fontWeight: "bold" }
              : {}
          }
        >
          {record.additional_feeExcel}
        </span>
      ),
    },
    // {
    //   title: "ตรวจเพิ่ม(ระบบ)",
    //   align: "center",
    //   render: (_, record) => (
    //     <span
    //       style={
    //         record.additional_feeExcel !== record.additional_fee
    //           ? { color: "red", fontWeight: "bold" }
    //           : {}
    //       }
    //     >
    //       {record.additional_fee}
    //     </span>
    //   ),
    // },
    {
      title: "รวม",
      align: "center",
      render: (text, record) => <>{record.totalExcel}</>,
    },

    // {
    //   title: "การจัดการ",
    //   align: "center",
    //   render: (text, record) => (
    //     <>
    //       <Popconfirm
    //         title="ลบสัญญา"
    //         description="คุณต้องการลบสัญญานี้ใช่หรือไม่ ?"
    //         onConfirm={() => {
    //           confirmDelete(record);
    //         }}
    //         onCancel={cancelDelete}
    //         okText="ยืนยัน"
    //         cancelText="ยกเลิก"
    //       >
    //         <Button style={{ fontSize: "16px", color: "red" }}>
    //           <DeleteOutlined />
    //         </Button>
    //       </Popconfirm>
    //     </>
    //   ),
    // },
  ];

  const onExpand = (expanded, record) => {
    if (expanded) {
      // เมื่อแถวถูกขยาย, ให้เพิ่ม key ของแถวนั้นลงใน expandedRowKeys
      setExpandedRowKeys([record.key]);
    } else {
      // เมื่อแถวถูกยุบ, ให้ลบ key ของแถวนั้นออกจาก expandedRowKeys
      setExpandedRowKeys([]);
    }
  };

  return (
    <Modal
      title={
        <span style={{ fontSize: 16, fontWeight: 400, color: "#333" }}>
          นำเข้าข้อมูลสืบทรัพย์ออนไลน์
        </span>
      }
      open={open}
      onCancel={() => handleCancel(true)}
      width={"auto"}
      keyboard={true}
      maskClosable={false}
      centered
      footer={[
        <Button danger key="cancel" onClick={() => handleCancel(true)}>
          ปิด
        </Button>,
        <Button
          color="blue"
          variant="outlined"
          key="ok"
          onClick={handleOk}
          loading={loading}
        >
          {/* {preData?.invalidData?.length > 0 || preData?.errors?.length > 0
            ? "ถัดไป"
            : "บันทึก"} */}
          ถัดไป
        </Button>,
      ]}
    >
      <Spin spinning={loading} size="large" tip=" Loading... ">
        <Row>
          <Col span={"24"} style={{ textAlign: "center", marginBottom: 20 }}>
            <Space size={8} direction="herizental" style={{ marginRight: 10 }}>
              <Input
                placeholder="ค้นหาสัญญา"
                onChange={(e) => onChangeInputSearch(e.target.value)}
                style={{ width: "100%", borderRadius: 8, marginBottom: 5 }}
                size="small"
                value={searchInput}
                suffix={<span style={{ marginRight: 5 }}>👤</span>}
              />
              <Button
                type="primary"
                icon={<SearchOutlined />}
                size="large"
                style={{
                  borderRadius: "8px",
                  boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
                  fontWeight: "bold",
                  background: "linear-gradient(135deg, #667eea, blue)",
                  border: "none",
                }}
                onClick={onQueryData}
                disabled={loading}
              >
                ค้นหา
              </Button>
              <Button
                icon={<RestOutlined />}
                size="large"
                onClick={() => {
                  setArrayTable(dataDefault);
                  setSearchInput(null);
                }}
                type="default"
                danger
                style={{
                  borderRadius: "8px",
                  boxShadow: "0px 4px 6px rgba(0,0,0,0.06)",
                  fontWeight: "bold",
                }}
                disabled={loading}
              >
                ล้าง
              </Button>
              <Button
                icon={<FileExcelOutlined />}
                size="large"
                onClick={handleExportExcel}
                type="default"
                style={{
                  borderRadius: "8px",
                  boxShadow: "0px 4px 6px rgba(0,0,0,0.06)",
                  fontWeight: "bold",
                  color: "#217346",
                  borderColor: "#217346",
                }}
                disabled={loading}
              >
                Export Excel
              </Button>
            </Space>
          </Col>
        </Row>
        <Card>
          <Table
            style={{ marginTop: "10px" }}
            size="small"
            columns={columns}
            dataSource={arrayTable}
            scroll={{ x: 850 }}
            footer={() => (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  padding: "8px 16px",
                  backgroundColor: "#fafafa",
                  borderRadius: "0 0 8px 8px",
                  border: "1px solid #f0f0f0",
                  borderTop: "none",
                  fontSize: "14px",
                }}
              >
                {/* ฝั่งซ้าย: สรุปจำนวน */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    height: "100%",
                  }}
                >
                  <span>จำนวนสัญญาที่ค้นหาทั้งหมด: </span>
                  <b style={{ color: "#1890ff", margin: "0 4px" }}>
                    {arrayTable.length}
                  </b>{" "}
                  รายการ
                </div>

                {/* ฝั่งขวา: 2 แถว */}
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 8 }}
                >
                  {/* แถวบน: ยอดจาก excel (sumMoney) */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 20,
                      fontWeight: 500,
                    }}
                  >
                    <span
                      style={{
                        background: "#e6f4ff",
                        color: "#1890ff",
                        borderRadius: 6,
                        padding: "1px 10px",
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                        fontSize: 13,
                      }}
                    >
                      ยอดจาก Excel
                    </span>
                    <div>
                      คัดทร.14:{" "}
                      <span style={{ color: "#1890ff" }}>
                        {sumMoney.tr14Sum?.toLocaleString()}
                      </span>
                    </div>
                    <div>
                      คัดทะเบียนครอบครัว:{" "}
                      <span style={{ color: "#1890ff" }}>
                        {sumMoney.familyRegistrationSum?.toLocaleString()}
                      </span>
                    </div>
                    <div>
                      ค่าดำเนินงาน:{" "}
                      <span style={{ color: "#1890ff" }}>
                        {sumMoney.operationFeeSum?.toLocaleString()}
                      </span>
                    </div>
                    <div>
                      ค่าตรวจเพิ่ม:{" "}
                      <span style={{ color: "#1890ff" }}>
                        {sumMoney.additionalFeeSum?.toLocaleString()}
                      </span>
                    </div>
                    <div
                      style={{
                        marginLeft: 10,
                        paddingLeft: 20,
                        borderLeft: "2px solid #d9d9d9",
                      }}
                    >
                      รวมทั้งสิ้น:{" "}
                      <b style={{ color: "#1890ff" }}>
                        {sumMoney.totalSum?.toLocaleString()}
                      </b>{" "}
                      บาท
                    </div>
                  </div>

                  {/* แถวล่าง: ยอดจากระบบ (sumMoneyEstimate) */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 20,
                      fontWeight: 500,
                    }}
                  >
                    <span
                      style={{
                        background: "#f6ffed",
                        color: "#52c41a",
                        borderRadius: 6,
                        padding: "1px 10px",
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                        fontSize: 13,
                      }}
                    >
                      ยอดจากระบบ
                    </span>
                    <div>
                      คัดทร.14:{" "}
                      <span style={{ color: "#52c41a" }}>
                        {sumMoneyEstimate.tr14Sum?.toLocaleString()}
                      </span>
                    </div>
                    <div>
                      คัดทะเบียนครอบครัว:{" "}
                      <span style={{ color: "#52c41a" }}>
                        {sumMoneyEstimate.familyRegistrationSum?.toLocaleString()}
                      </span>
                    </div>
                    <div>
                      ค่าดำเนินงาน:{" "}
                      <span style={{ color: "#52c41a" }}>
                        {sumMoneyEstimate.operationFeeSum?.toLocaleString()}
                      </span>
                    </div>
                    <div>
                      ค่าตรวจเพิ่ม:{" "}
                      <span style={{ color: "#52c41a" }}>
                        {sumMoneyEstimate.additionalFeeSum?.toLocaleString()}
                      </span>
                    </div>
                    <div
                      style={{
                        marginLeft: 10,
                        paddingLeft: 20,
                        borderLeft: "2px solid #d9d9d9",
                      }}
                    >
                      รวมทั้งสิ้น:{" "}
                      <b style={{ color: "#52c41a" }}>
                        {sumMoneyEstimate.totalSum?.toLocaleString()}
                      </b>{" "}
                      บาท
                    </div>
                  </div>
                </div>
              </div>
            )}
            expandable={{
              expandedRowRender: (record) => (
                <div
                  style={{
                    padding: "10px 16px",
                    background: "#f9f9f9",
                    borderLeft: "3px solid #1890ff",
                    borderRadius: "0 6px 6px 0",
                    margin: "4px 0",
                  }}
                >
                  <span
                    style={{
                      fontSize: 13,
                      color: "#444",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                    }}
                  >
                    📝 {record.mark}
                  </span>
                </div>
              ),
              rowExpandable: (record) => !!record.mark && record.mark !== "",
              expandedRowKeys, // เก็บ state ของ row ที่ขยาย
              onExpand, // ฟังก์ชันที่ควบคุมการขยาย
            }}
            rowKey="key"
          />
        </Card>
        {isModalInvalid ? (
          <AddRelationAssets
            open={isModalInvalid}
            close={setIsModalInvalid}
            data={preData.allData}
            defaultData={preData}
            geoRawData={geoRawData}
            func={handleCancel}
            sumMoney={sumMoney}
            sumMoneyEstimate={sumMoneyEstimate}
          />
        ) : null}
      </Spin>
    </Modal>
  );
};
export default ImportInvestigate;
